import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  TablePagination,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material';
import { Delete, Edit, Add, FilterAltOff, CheckCircle, ErrorOutline } from '@mui/icons-material';
import { jobPortalsAPI, citiesAPI } from '../../services/api';
import browserGlobal from '../../utils/browserGlobal';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';

function getApiTotalCount(apiTotal, fallbackLength) {
  if (apiTotal != null && Number(apiTotal) >= 0) return Number(apiTotal);
  return fallbackLength;
}

function getSubmitButtonLabel(isSubmitting, selectedPortal, translate) {
  if (isSubmitting) return translate('common.saving');
  if (selectedPortal) return translate('common.update');
  return translate('common.add');
}

function renderYesNoChip(value, translate) {
  if (value) {
    return <Chip label={translate('common.yes')} color="success" size="small" icon={<CheckCircle />} />;
  }
  return <Chip label={translate('common.no')} color="default" size="small" icon={<ErrorOutline />} />;
}

const JobPortals = () => {
  const { t } = useLanguage();
  const adminUser = getAdminUser();
  const isSuperAdmin = /super\s*admin/i.test(adminUser.role || '');

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobPortals, setJobPortals] = useState([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });
  const [cities, setCities] = useState([]);
  const [selectedPortal, setSelectedPortal] = useState(null);
  const [open, setOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    cityId: ''
  });

  const [debouncedTitle, setDebouncedTitle] = useState('');
  const debouncedTitleRef = useRef(debouncedTitle);
  useEffect(() => {
    debouncedTitleRef.current = debouncedTitle;
  }, [debouncedTitle]);

  useEffect(() => {
    const id = browserGlobal.setTimeout(() => {
      const next = (filters.search || '').trim();
      const prev = debouncedTitleRef.current;
      if (prev !== next) {
        setPage(0);
      }
      setDebouncedTitle(next);
    }, 400);
    return () => browserGlobal.clearTimeout(id);
  }, [filters.search]);

  const [formData, setFormData] = useState({
    id: 0,
    cityId: '',
    title: '',
    url: '',
    isRecomended: true,
    isEnabled: true
  });
  const [errors, setErrors] = useState({
    cityId: '',
    title: '',
    url: ''
  });
  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };

  /* ------------------------ Fetch helpers ----------------------- */
  const fetchCities = async () => {
    try {
      const res = await citiesAPI.getCities();
      if (res?.data?.success) {
        const list = res.data.payload || [];
        setCities(list);
        if (list.length && !filters.cityId) {
          setFilters(prev => ({ ...prev, cityId: list[0].id }));
        }
      }
    } catch (e) {
      console.error('Error fetching cities:', e);
    }
  };

  const fetchJobPortals = useCallback(async () => {
    if (!filters.cityId) return;
    setIsLoading(true);
    try {
      const res = await jobPortalsAPI.getJobPortals(filters.cityId, page, rowsPerPage, {
        title: debouncedTitle,
      });
      if (res?.data?.success && Array.isArray(res.data.payload)) {
        const list = res.data.payload;
        setJobPortals(list);
        setTotalCount(getApiTotalCount(res?.data?.totalRecord, list.length));
      } else {
        setJobPortals([]);
        setTotalCount(0);
      }
    } catch (e) {
      console.error('Error fetching job portals:', e);
      setJobPortals([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [filters.cityId, page, rowsPerPage, debouncedTitle]);

  /* ------------------------- Effects ---------------------------- */
  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    fetchJobPortals();
  }, [fetchJobPortals]);

  const visibleJobPortals = useMemo(() => {
    const q = debouncedTitle.trim().toLowerCase();
    if (!q) return jobPortals;
    const hay = (v) => String(v ?? '').toLowerCase();
    return jobPortals.filter((row) => hay(row.title).includes(q) || hay(row.Title).includes(q));
  }, [jobPortals, debouncedTitle]);

  /* ----------------------- UI Handlers -------------------------- */
  const handleClearFilters = () => {
    setFilters((prev) => ({ ...prev, search: '' }));
    setDebouncedTitle('');
    setPage(0);
  };

  const handleOpen = (portal = null) => {
    setErrors({ cityId: '', title: '', url: '' });
    if (portal) {
      setSelectedPortal(portal);
      setFormData({
        id: portal.id,
        cityId: portal.cityId,
        title: portal.title,
        url: portal.url,
        isRecomended: portal.isRecomended,
        isEnabled: portal.isEnabled
      });
    } else {
      setSelectedPortal(null);
      setFormData({
        id: 0,
        cityId: filters.cityId || '',
        title: '',
        url: '',
        isRecomended: true,
        isEnabled: true
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPortal(null);
    setErrors({ cityId: '', title: '', url: '' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'cityId' || name === 'title' || name === 'url') {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const validateForm = () => {
    const nextErrors = { cityId: '', title: '', url: '' };
    let hasError = false;

    if (!formData.cityId) {
      nextErrors.cityId = t('contacts.errFieldRequired') || 'City is required';
      hasError = true;
    }
    if (!formData.title?.trim()) {
      nextErrors.title = t('contacts.errFieldRequired') || 'Title is required';
      hasError = true;
    }
    if (!formData.url?.trim()) {
      nextErrors.url = t('contacts.errFieldRequired') || 'URL is required';
      hasError = true;
    } else {
      try {
        new URL(formData.url);
      } catch (e) {
        nextErrors.url = t('common.urlInvalid') || 'Please enter a valid URL';
        hasError = true;
      }
    }

    setErrors(nextErrors);
    return !hasError;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    try {
      if (selectedPortal) {
        await jobPortalsAPI.updateJobPortal(formData);
        showToast(t('jobs.toastUpdated'), 'success');
      } else {
        await jobPortalsAPI.createJobPortal(formData);
        showToast(t('jobs.toastCreated'), 'success');
      }
      fetchJobPortals();
      handleClose();
    } catch (e) {
      console.error('Error saving portal:', e);
      showToast(t('jobs.toastSaveError'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!browserGlobal.confirm(t('jobs.confirmDelete'))) return;
    try {
      await jobPortalsAPI.deleteJobPortal(id);
      showToast(t('jobs.toastDeleted'), 'success');
      fetchJobPortals();
    } catch (e) {
      console.error('Error deleting portal:', e);
      showToast(t('jobs.toastDeleteError'), 'error');
    }
  };

  /* --------------------------- Render --------------------------- */
  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={2}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" color="primary">{t('jobs.title')}</Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg,#1b5a5c 0%,#0a2829 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg,#328284 0%,#0F3D3E 100%)' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('jobs.add')}
          </Button>
        )}
      </Box>
      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: '#fff',
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(124,58,237,0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('districts.filterCity')}</InputLabel>
              <Select
                value={filters.cityId}
                label={t('districts.filterCity')}
                onChange={(e) => { setFilters(prev => ({ ...prev, cityId: e.target.value })); setPage(0); }}
              >
                {cities.map(city => (
                  <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              size="small"
              fullWidth
              label={t('common.title')}
              placeholder={t('jobs.searchPh')}
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md="auto"
            sx={{ display: 'flex', alignItems: 'center', ml: { md: 'auto' } }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOff />}
              onClick={handleClearFilters}
              sx={clearFilterButtonSx}
            >
              {t('common.clear')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Table */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}><CircularProgress sx={{ color: '#0F3D3E' }} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: '0 2px 4px rgba(124,58,237,0.05)', borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.title')}</TableCell>
                <TableCell>{t('cityReg.fieldUrl')}</TableCell>
                <TableCell>{t('jobs.colRecommended')}</TableCell>
                <TableCell>{t('accommodation.fieldEnabled')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleJobPortals.map((row) => (
                <TableRow key={row.id} sx={{ '&:hover': { bgcolor: '#ebf4f4' } }}>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>
                    <a href={row.url} target="_blank" rel="noreferrer">{row.url}</a>
                  </TableCell>
                  <TableCell>
                    {renderYesNoChip(row.isRecomended, t)}
                  </TableCell>
                  <TableCell>
                    {renderYesNoChip(row.isEnabled, t)}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpen(row)} color="primary"><Edit /></IconButton>
                    {isSuperAdmin && (
                      <IconButton onClick={() => handleDelete(row.id)} color="error"><Delete /></IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(Number.parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5,10,25,50]}
            labelRowsPerPage={t('common.rowsPerPage')}
          />
        </TableContainer>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedPortal ? t('jobs.dialogEdit') : t('jobs.dialogAdd')}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth required error={Boolean(errors.cityId)}>
              <InputLabel>{t('common.city')}</InputLabel>
              <Select
                label={t('common.city')}
                name="cityId"
                value={formData.cityId}
                onChange={handleChange}
              >
                {cities.map(city => (
                  <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                ))}
              </Select>
              {errors.cityId ? <FormHelperText>{errors.cityId}</FormHelperText> : null}
            </FormControl>
            <TextField 
              label={t('common.title')} 
              name="title" 
              value={formData.title} 
              onChange={handleChange} 
              required
              error={Boolean(errors.title)}
              helperText={errors.title || undefined}
              fullWidth 
            />
            <TextField 
              label={t('cityReg.fieldUrl')} 
              name="url" 
              value={formData.url} 
              onChange={handleChange} 
              required
              error={Boolean(errors.url)}
              helperText={errors.url || undefined}
              fullWidth 
            />
            <FormControlLabel control={<Switch checked={formData.isRecomended} name="isRecomended" onChange={handleSwitchChange} />} label={t('jobs.switchRecommended')} />
            <FormControlLabel control={<Switch checked={formData.isEnabled} name="isEnabled" onChange={handleSwitchChange} />} label={t('accommodation.fieldEnabled')} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting} startIcon={isSubmitting ? <CircularProgress size={20} /> : null} sx={{ background: 'linear-gradient(135deg,#1b5a5c 0%,#0a2829 100%)', color: '#fff' }}>
            {getSubmitButtonLabel(isSubmitting, selectedPortal, t)}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JobPortals;
